import { AfterViewInit, Component, Input, OnChanges, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { EncodeHtmlSymbolsPipe } from '@sb-tools/typescript/pipes/encode-html-symbols.pipe';
import { MentionBlot } from './mention-blot';
import { QuillQuickAccessService } from '@sb-tools/typescript/services/quill/quill-quick-access.service';
import { QuillQuickAccessOptions } from '@sb-tools/typescript/models/quill-quick-access-options.model';
import { BaseUserWithRole } from '@sb-tools/typescript/models/base-user-with-role.model';
import Quill from 'quill';
import './mention-blot';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { UserDataService } from '@sb-tools/typescript/services/user-data.service';
import { Subject } from 'rxjs';
import { MentionItem } from '../../../core/models/mention-item.model';
import { FacebookMentionUserModel } from '../../../core/models/facebook-mention-user-model.model';
import { MentionType } from '../../../core/enums/mention-type.enum';
import { NgClass } from '@angular/common';
import { UserAvatarComponent } from '../../../../sb-modules/Frontend-User-Avatar-Module/user-avatar.component';
import { InlineSVGComponent } from '../../../../sb-modules/Frontend-Inline-SVG-Module/inline-svg.component';
import { TranslateModule } from '@ngx-translate/core';
import { TruncatePipe } from '../../../../sb-tools/typescript/pipes/truncate.pipe';

Quill.register({ 'formats/mention': MentionBlot });

@Component({
    selector: 'app-sb-quill-mention',
    templateUrl: './sb-quill-mention.component.html',
    styleUrls: ['./sb-quill-mention.component.scss'],
    imports: [NgClass, UserAvatarComponent, InlineSVGComponent, TranslateModule, TruncatePipe],
})
export class SbQuillMentionComponent implements OnChanges, OnInit, AfterViewInit, OnDestroy {
    @ViewChild('mentionContainer') mentionContainerElement;
    @ViewChild('mentionList') mentionListElement;

    @Input() isFacebookMention = false;
    @Input() parentContainerId;
    @Input() quillEditor: Quill;
    @Input() usersToMention: BaseUserWithRole[] | FacebookMentionUserModel[];

    filteredMentionItems: MentionItem[] = [];
    groupsMentionItems: MentionItem[] = [];
    mentionItems: MentionItem[] = [];
    mentionTypeEnum = MentionType;

    private cursorPos;
    private isOpen = false;
    private itemIndex;
    private mentionCharPos;
    private mentionContainer;
    private mentionList;
    private options: QuillQuickAccessOptions = {
        dropdownItemHeight: 38,
        dropdownListMaxHeight: 300,
        denotationChar: ['@'],
        showDenotationChar: true,
        allowedChars: /^[@A-Za-z\sÆØÅæøåÄÖäö0-9!#$%&+_~\-.]*$/,
        minChars: 0,
        maxChars: 31,
        offsetTop: 5,
        offsetLeft: 0,
        isFixedPosition: true,
        isolateCharacter: false,
        fixContainerToQuill: false,
        defaultMenuOrientation: 'top',
        blotName: 'mention',
        dataAttributes: ['id', 'value', 'denotationChar', 'link', 'target'],
        linkTarget: '_blank',

        // Style options
        listItemClass: 'ql-mention-list-item',
        dropdownContainerClass: 'ql-mention-list-container',
        itemsListClass: 'ql-mention-list',
        spaceAfterInsert: true,
        parentContainerId: null,
    };
    private unsubscriber = new Subject<void>();

    constructor(
        private encodeHtmlSymbolsPipe: EncodeHtmlSymbolsPipe,
        private quillQuickAccessService: QuillQuickAccessService,
        private userDataService: UserDataService,
    ) {}

    ngAfterViewInit() {
        this.mentionContainer = this.mentionContainerElement.nativeElement;
        this.mentionList = this.mentionListElement.nativeElement;

        this.quillQuickAccessService.initDropdownStyles(this.mentionContainer, this.options);
    }

    ngOnChanges() {
        this.mentionItems = [];

        if (this.usersToMention) {
            this.usersToMention.forEach((user) => {
                user.value = user.name ? user.name + ' ' + user.email : user.email;
            });

            this.usersToMention.forEach((user) => {
                this.mentionItems.push({
                    denotationChar: '@',
                    type: MentionType.user,
                    user,
                    valuesForTextSearch: this.getValuesForTextSearch([user.name, user.email]),
                    value: user.name || user.email,
                    id: user.id,
                });
            });
        }

        this.mentionItems.push(...this.groupsMentionItems);

        if (this.quillEditor) {
            const keyboardBindings = {
                textChangeHandler: this.onTextChange.bind(this),
                selectionChangeHandler: this.onSelectionChange.bind(this),
                enterHandler: this.selectHandler.bind(this),
                escapeHandler: this.hideMentionList.bind(this),
                upHandler: this.upHandler.bind(this),
                downHandler: this.downHandler.bind(this),
            };
            this.quillQuickAccessService.addQuickAccessBindings(this.quillEditor, keyboardBindings);
        }

        this.options.parentContainerId = this.parentContainerId;
    }

    ngOnInit() {
        if (!this.isFacebookMention) {
            this.userDataService
                .getUserGroups()
                .pipe(takeUntil(this.unsubscriber))
                .subscribe((groups) => {
                    groups.forEach((group) => {
                        this.groupsMentionItems.push({
                            denotationChar: '@',
                            type: MentionType.group,
                            group,
                            valuesForTextSearch: this.getValuesForTextSearch([group.name]),
                            value: group.name,
                            id: group.id,
                        });
                    });

                    this.mentionItems.push(...this.groupsMentionItems);
                });
        }
    }

    hideMentionList(): void {
        this.quillQuickAccessService.hideDropdown(this.mentionContainer);
        this.isOpen = false;
    }

    onItemClick(event, index) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.itemIndex = index;
        this.selectItem();
    }

    selectItem() {
        const data = this.getItemData();
        this.insertItem(data);
        this.hideMentionList();
    }

    private downHandler() {
        if (this.isOpen) {
            this.nextItem();
            return false;
        }
        return true;
    }

    private getFilteredMentionItems(searchTerm: string): MentionItem[] {
        const preparedSearchTerm = searchTerm.trim().toLowerCase();
        const count = 50;

        if (preparedSearchTerm.length > 0) {
            const filtered: MentionItem[] = [];
            const subTerms = preparedSearchTerm.split(/[\s,_\-.]+/);

            for (const item of this.mentionItems) {
                const isAppropriate = subTerms.every((subTerm) =>
                    item.valuesForTextSearch.some((val) => val.toLowerCase().indexOf(subTerm) === 0),
                );

                if (isAppropriate) {
                    filtered.push(item);
                }

                if (filtered.length === count) {
                    break;
                }
            }

            return filtered;
        }

        return this.mentionItems.slice(0, count);
    }

    private getItemData() {
        const { link } = this.filteredMentionItems[this.itemIndex];
        const item = this.filteredMentionItems[this.itemIndex];
        const hasLinkValue = typeof link !== 'undefined';
        const itemTarget = item.target;
        if (hasLinkValue) {
            item.value = `<a href="${link}" target="${itemTarget || this.options.linkTarget}">${item.value}`;
        }
        return item;
    }

    private getMentionCharIndex(text, mentionDenotationChars) {
        return mentionDenotationChars.reduce(
            (prev, mentionChar) => {
                const mentionCharIndex = text.lastIndexOf(mentionChar);

                if (mentionCharIndex > prev.mentionCharIndex) {
                    return {
                        mentionChar,
                        mentionCharIndex,
                    };
                }
                return {
                    mentionChar: prev.mentionChar,
                    mentionCharIndex: prev.mentionCharIndex,
                };
            },
            { mentionChar: null, mentionCharIndex: -1 },
        );
    }

    private getValuesForTextSearch(strings: string[]): string[] {
        const result: string[] = [];

        strings.forEach((stringItem) => {
            if (stringItem) {
                const stringItemParts = stringItem.split(/[\s,_\-.]+/);
                result.push(
                    ...stringItemParts.filter((stringItemPart) => {
                        return !!stringItemPart;
                    }),
                );
            }
        });

        return result;
    }

    private hasValidChars(text, allowedChars) {
        return allowedChars.test(text);
    }

    private hasValidMentionCharIndex(mentionCharIndex, text, isolateChar) {
        if (mentionCharIndex > -1) {
            if (isolateChar && !(mentionCharIndex === 0 || !!text[mentionCharIndex - 1].match(/\s/g))) {
                return false;
            }
            return true;
        }
        return false;
    }

    private highlightItem(scrollItemInView = true) {
        this.filteredMentionItems.forEach((item) => {
            item.isSelected = false;
        });

        this.filteredMentionItems[this.itemIndex].isSelected = true;

        if (!this.isOpen) {
            return;
        }

        if (scrollItemInView) {
            this.quillQuickAccessService.scrollActiveDropdownItemToView(
                this.mentionContainer,
                this.mentionList,
                this.itemIndex,
            );
        }
    }

    private insertItem(data) {
        const temp = Object.assign({}, data);
        // temp.name = this.encodeHtmlSymbolsPipe.transform(data.name);
        // temp.email = this.encodeHtmlSymbolsPipe.transform(data.email);
        temp.value = this.encodeHtmlSymbolsPipe.transform(data.value);

        const render = temp;
        if (render === null) {
            return;
        }
        if (!this.options.showDenotationChar) {
            render.denotationChar = '';
        }

        const prevMentionCharPos = this.mentionCharPos;

        this.quillEditor.deleteText(this.mentionCharPos, this.cursorPos - this.mentionCharPos, Quill.sources.USER);
        this.quillEditor.insertEmbed(prevMentionCharPos, this.options.blotName, render, Quill.sources.USER);
        if (this.options.spaceAfterInsert) {
            this.quillEditor.insertText(prevMentionCharPos + 1, ' ', Quill.sources.USER);
            // setSelection here sets cursor position
            this.quillEditor.setSelection(prevMentionCharPos + 2, Quill.sources.USER);
        } else {
            this.quillEditor.setSelection(prevMentionCharPos + 1, Quill.sources.USER);
        }

        this.hideMentionList();
    }

    private nextItem() {
        this.itemIndex = (this.itemIndex + 1) % this.filteredMentionItems.length;
        this.highlightItem();
    }

    private onSelectionChange(range) {
        if (range && range.length === 0) {
            this.onSomethingChange();
        } else {
            this.hideMentionList();
        }
    }

    private onSomethingChange() {
        const range = this.quillEditor.getSelection();
        if (range == null) {
            return;
        }

        this.cursorPos = range.index;
        const textBeforeCursor = this.quillQuickAccessService.getTextBeforeCursor(
            this.quillEditor,
            this.cursorPos,
            this.options.maxChars,
        );

        const { mentionChar, mentionCharIndex } = this.getMentionCharIndex(
            textBeforeCursor,
            this.options.denotationChar,
        );

        if (this.hasValidMentionCharIndex(mentionCharIndex, textBeforeCursor, this.options.isolateCharacter)) {
            const mentionCharPos = this.cursorPos - (textBeforeCursor.length - mentionCharIndex);
            this.mentionCharPos = mentionCharPos;
            const textAfter = textBeforeCursor.substring(mentionCharIndex + mentionChar.length);

            if (textAfter.length >= this.options.minChars && this.hasValidChars(textAfter, this.options.allowedChars)) {
                this.renderList(textAfter);
            } else {
                this.hideMentionList();
            }
        } else {
            this.hideMentionList();
        }
    }

    private onTextChange(delta, oldDelta, source) {
        if (source === 'user') {
            // settimeout need for fixing problem with updating search results when pasting text in mention
            setTimeout(() => {
                this.onSomethingChange();
            }, 1);
        }
    }

    private prevItem() {
        this.itemIndex = (this.itemIndex + this.filteredMentionItems.length - 1) % this.filteredMentionItems.length;
        this.highlightItem();
    }

    private renderList(searchTerm: string): void {
        this.filteredMentionItems = this.getFilteredMentionItems(searchTerm);

        if (this.filteredMentionItems.length) {
            this.itemIndex = 0;
            this.highlightItem();
            setTimeout(() => {
                this.showMentionList(this.filteredMentionItems.length);
            }, 100);
        } else {
            this.hideMentionList();
        }
    }

    private selectHandler() {
        if (this.isOpen) {
            this.selectItem();
            return false;
        }
        return true;
    }

    private showMentionList(usersLength: number) {
        this.quillQuickAccessService.showDropdown(
            this.quillEditor,
            this.options,
            usersLength,
            this.mentionContainer,
            this.mentionCharPos,
        );

        this.isOpen = true;
    }

    private upHandler() {
        if (this.isOpen) {
            this.prevItem();
            return false;
        }
        return true;
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
